import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAtom } from 'jotai'
import { useAppDispatch } from '../../redux/app/hooks'
import { getBasketApi } from '../../redux/slices/basket/basketApi'
import { syncActiveBasketRedux } from '../../redux/slices/basket/basketSlice'
import { ClientStorage } from '../../base/storage'
import { refreshBasketAfterChat } from './chatBasketSync'
import classes from './ChatWidget.module.scss'
import {
  ChatMessageApi,
  ChatButtonsApi,
  ChatFeedbackApi,
  ChatHistoryApi,
  ChatHistoryDeleteApi,
  ChatHistoryMessagesApi,
  ChatPendingSurveyApi,
} from './chatService'
import {
  ButtonDefinition,
  ChatBubble,
  ChatBubbleExtras,
  ChatForm,
  ChatHistoryItem,
  ChatMessageRequest,
  ChatMessageResponse,
  ChatPromoVideo,
  CommissionMode,
  FeedbackVote,
  FormOption,
} from './types'
import { isChatbotEnabledForCurrentUser, isLoggedIn } from '../../base/proxy/authenticate'
import { isPublicCustomerPath } from '../../routes/publicPaths'
import ChatHeader from './ChatHeader'
import ChatInput from './ChatInput'
import CargoTrackingModal from './CargoTrackingModal'
import CommissionRatesModal from './CommissionRatesModal'
import FabGlow from './FabGlow'
import { giniIntroFlightAtom } from './giniIntroFlight'
import GiniIntroFlightOverlay from './GiniIntroFlightOverlay'
import GuaranteeInquiryModal from './GuaranteeInquiryModal'
import HistorySidebar from './HistorySidebar'
import MessageBubble from './MessageBubble'
import { normalizeFeedbackState } from './messageFeedback'
import PromoVideoModal from './PromoVideoModal'
import QuickLinksSidebar from './QuickLinksSidebar'
import SerialNumberCheckModal from './SerialNumberCheckModal'
import TypingIndicator from './TypingIndicator'
import { IconBot, IconChevron, IconStar } from './icons'
import { normalizeButtons, normalizeChatMessageResponse } from './chatMappers'
import { anchorScrollTop, replyScrollAnchorId } from './chatScroll'
import { getChatErrorMessage } from './chatFormatters'
import { structureChatMessage } from './chatTextStructurer'
import { applyIfCurrentGeneration, historyMessagesToBubbles, isGenerationCurrent, resumeOnOpen } from './historyMappers'
import IntroCard, { IntroThemeAction } from './IntroCard'
import IntroNudge from './IntroNudge'
import { resolveIntroPlan } from './introState'
import {
  isIntroSeen,
  isNudgeDismissed,
  isShownThisSession,
  markIntroSeen,
  markNudgeDismissed,
  markShownThisSession,
} from './introStorage'
import {
  hasPendingSurvey,
  isSurveyInviteActive,
  isSurveyNudgeClosed,
  markSurveyNudgeClosed,
  withSurveyInvite,
} from './surveyInvite'
import SurveyNudge from './SurveyNudge'
import { useDraggableFab } from './useDraggableFab'

/** needsInput=true butonlar için: kullanıcı girdisi hangi parametre anahtarıyla gönderilsin. */
const PARAM_KEY: Record<string, string> = {
  searchUrun: 'q',
  getStok: 'code',
  getBayiFiyati: 'code',
  getUrunDetay: 'code',
  getEnCokSatinAldiklarim: 'source',
  // XML Stok Sorgu: ürün kodu LLM'e gitmeden doğrudan araca slot olarak iletilir.
  getXmlStok: 'urunKodu',
}

/** needsInput butonları için özel soru metni; yoksa "<Label> için bilgiyi yazar mısınız?" kullanılır. */
const PENDING_PROMPT: Record<string, string> = {
  getEnCokSatinAldiklarim:
    'En çok satın aldığınız ürünler için B2B siparişlerinizi mi yoksa SAP siparişlerinizi mi baz alayım? (B2B / SAP yazabilirsiniz)',
}

/**
 * Çok adımlı buton soruları: toplanan parametrelere bakıp bir sonraki adımı (varsa) döner.
 * Backend her hâlükârda eksik slot'u kendi sorar; bu yalnızca buton akışını akıcı tutar.
 */
const NEXT_STEP: Record<string, (params: Record<string, string>) => { paramKey: string; prompt: string } | null> = {
  getEnCokSatinAldiklarim: params => {
    const source = (params.source ?? '').toLowerCase()
    // SAP satırları portal kullanıcısı taşımaz → kapsam yalnız B2B'de sorulur.
    if (!source.includes('b2b') || params.scope) return null
    return {
      paramKey: 'scope',
      prompt:
        'B2B siparişlerinde kapsamı nasıl alayım: yalnız kendi siparişleriniz mi, bayinizin tüm siparişleri mi? (kendi / bayi yazabilirsiniz)',
    }
  },
}

/** "Komisyon Oranları" butonunun backend intent'i; modal seçimi de aynı intent'e post edilir. */
const COMMISSION_INTENT = 'getKomisyonOranlari'

type FeatureModalName = 'cargo' | 'serialNumber' | 'guarantee' | 'commission' | 'promoVideo'

const FEATURE_MODAL_BY_INTENT: Record<string, FeatureModalName> = {
  getKargoTakip: 'cargo',
  getSeriNoKontrol: 'serialNumber',
  getGarantiSorgu: 'guarantee',
  [COMMISSION_INTENT]: 'commission',
}

const GREETING =
  'Merhaba! 👋 Ben B-Gini, dijital asistanınız. Size nasıl yardımcı olabilirim?\nSorunuzu yazabilir ya da hızlı bağlantıları kullanabilirsiniz.'

/**
 * Tanıtım kartı gösterilirken kullanılan karşılama. Asistanın adı burada tekrarlanmaz — kimliği
 * kartın başlığı taşır; balon "konuşan" cümledir.
 */
const INTRO_GREETING = 'Merhaba! 👋 Size nasıl yardımcı olabileceğimi göstereyim.'

/**
 * Karşılama balonları tek yerden kurulur: üç çağrı noktası da (taban karşılama, tanıtım
 * karşılaması, "Yeni Sohbet") aynı metni taşısın diye. Modül seviyesinde durur ki efekt
 * bağımlılıklarına girmesin. Tanıtım videosu kartı karşılamadan kaldırıldı; sunucu `promoVideo`
 * gönderirse (ör. "merhaba" yanıtı) oynatıcı yine çalışır.
 */
const greetingBubble = (id: number): ChatBubble => ({ id, role: 'bot', text: GREETING, welcome: 'greeting' })

const introGreetingBubble = (id: number): ChatBubble => ({
  id,
  role: 'bot',
  text: INTRO_GREETING,
  welcome: 'greeting',
})

/** Otomatik açılmadan önceki bekleme: sayfanın yerleşmesini bekler. */
const INTRO_AUTO_OPEN_DELAY_MS = 2000

/** Karşılama + kart yerine oturmadan önce botun "yazıyor" göründüğü süre. */
const INTRO_TYPING_MS = 900

/** Hatırlatma balonunun kendiliğinden solma süresi. */
const INTRO_NUDGE_TTL_MS = 6000

/** Uçuş sonrası FAB'in "pop" süresi (`.fabArrive` animasyonuyla aynı ya da biraz uzun). */
const FAB_ARRIVE_MS = 500

/** Viewport medya sorgusu (mobil eşiği / geniş ekran eşiği). */
function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches)
  useEffect(() => {
    const mql = window.matchMedia(query)
    const onChange = () => setMatches(mql.matches)
    onChange()
    // Eski Safari sürümleri addEventListener desteklemez
    if (mql.addEventListener) mql.addEventListener('change', onChange)
    else mql.addListener(onChange)
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange)
      else mql.removeListener(onChange)
    }
  }, [query])
  return matches
}

export default function ChatWidget() {
  const location = useLocation()
  const dispatch = useAppDispatch()
  const [open, setOpen] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [messages, setMessages] = useState<ChatBubble[]>([])
  const [buttons, setButtons] = useState<ButtonDefinition[]>([])
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [featureSending, setFeatureSending] = useState(false)
  const [pending, setPending] = useState<{
    intent: string
    label: string
    paramKey: string
    /** Önceki adımlarda toplanan parametreler (çok adımlı sorular için birlikte gönderilir). */
    carried?: Record<string, string>
  } | null>(null)
  const [activeModal, setActiveModal] = useState<FeatureModalName | null>(null)
  /** Oynatıcı modalına verilecek video; kapak kartına tıklanınca dolar. */
  const [promoVideo, setPromoVideo] = useState<ChatPromoVideo | null>(null)

  // Yerleşim modları + oturum içi sohbet geçmişi
  const [expanded, setExpanded] = useState(false)
  const [leftOpen, setLeftOpen] = useState(false)
  const [rightOpen, setRightOpen] = useState(false)
  const [history, setHistory] = useState<ChatHistoryItem[]>([])
  const [activeConversationId, setActiveConversationId] = useState<number | null>(null)

  // İlk temas tanıtımı
  const [showIntro, setShowIntro] = useState(false)
  const [nudgeVisible, setNudgeVisible] = useState(false)
  const [introEnter, setIntroEnter] = useState(false)
  const [introTyping, setIntroTyping] = useState(false)

  /** Bayinin cevaplamadığı B-Gini anketi var → karşılamanın altına davet balonu eklenir. */
  const [surveyPending, setSurveyPending] = useState(false)
  /** Ana sayfadaki anket baloncuğu bu sekmede kapatıldı (ya da ankete gidildi). */
  const [surveyNudgeClosed, setSurveyNudgeClosed] = useState(isSurveyNudgeClosed)

  const bodyRef = useRef<HTMLDivElement>(null)
  /** Faz-44: yanıtı bu oturumda canlı gelen son kullanıcı mesajı; kaydırma hizası bunun üzerinden kurulur. */
  const [liveReplyUserId, setLiveReplyUserId] = useState<number | null>(null)
  /** Kaydırma efektinin gördüğü son panel açıklık durumu (açılış anı → en alt). */
  const scrollPanelOpenRef = useRef(false)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const fabRef = useRef<HTMLButtonElement>(null)
  /** Kapalıyken FAB + baloncukları taşıyan kök; sürükleme başlangıcı onun ekrandaki yeridir. */
  const rootRef = useRef<HTMLDivElement>(null)
  const idRef = useRef(1)
  const conversationIdRef = useRef<number | null>(null)
  const conversationGenerationRef = useRef(0)
  /** Tanıtım kartı gösterilmeyi bekliyor. Ref: yükleme efektinin kapanışı bayat değer görmesin. */
  const introPendingRef = useRef(false)
  /** Plan yalnız bir kez kurulur (widget login'de SPA gezinmesiyle görünür hâle gelebilir). */
  const introPlannedRef = useRef(false)
  /** Panel davetsiz açıldı → odak input'a çalınmaz. */
  const autoOpenedRef = useRef(false)
  /** Otomatik açılma / balon solma zamanlayıcısı. Yalnız unmount'ta temizlenir. */
  const introTimerRef = useRef<number | null>(null)

  // Gini Tanıtım videosu (spec §12.4): popup açıkken (`playing`) ve logo uçarken (`flying`) tanıtım
  // baloncuğu, anket baloncuğu ve otomatik açılış bekler.
  const [introFlight, setIntroFlight] = useAtom(giniIntroFlightAtom)
  const introHeld = introFlight !== 'idle'
  /** Zamanlayıcı geri çağrıları güncel değeri görsün diye ref'te de tutulur. */
  const introHeldRef = useRef(introHeld)
  introHeldRef.current = introHeld
  /** Otomatik açılış beklemeye düştü; video/uçuş bitince yeniden kurulur. */
  const deferredAutoOpenRef = useRef(false)
  /** Uçuş bitti → FAB kısa bir "pop" ile görünür. */
  const [fabArrive, setFabArrive] = useState(false)

  const isMobile = useMediaQuery('(max-width: 640px)')
  const isWide = useMediaQuery('(min-width: 1024px)')
  // Sidebar'lar yalnız geniş ekrandaki tam boyut modunda akış içindedir; diğer her durumda overlay
  const overlaySidebars = !(expanded && isWide) || isMobile
  const prevOverlayRef = useRef(overlaySidebars)
  const isBusy = sending || featureSending
  // Sürüklenebilir logo (spec §5): yalnız kapalı başlatıcı taşınır, panel açıkken kök bugünkü yerine döner.
  const fabDrag = useDraggableFab(rootRef, !open)

  // Hook'ların üstünde: ilk temas planı efekti bunu bağımlılık olarak kullanıyor.
  // Erişim kararı backend'den gelir (bayi alt tipi); burada yalnızca bayrak okunur.
  // Kart onay sayfası müşteriye açıktır; widget orada oturumlu çağrı yapmamalı.
  const shouldHideWidget =
    !isLoggedIn() ||
    !isChatbotEnabledForCurrentUser() ||
    location.pathname === '/xml/xml-request' ||
    isPublicCustomerPath(location.pathname)

  // Hızlı Bağlantılar CTA'sı / tanıtım kartı karşılamanın altında durur; anket daveti varsa onun altında.
  const welcomeAnchorIdx = messages[1]?.welcome === 'surveyInvite' ? 1 : 0

  /** Balonu ekler ve id'sini döner (Faz-44: yanıt gelince hizalanacak kullanıcı mesajı bu id ile bulunur). */
  const addBubble = useCallback((role: 'user' | 'bot', text: string, extras?: ChatBubbleExtras): number => {
    const id = idRef.current++
    setMessages(prev => [...prev, { id, role, text, ...extras }])
    return id
  }, [])

  // İlk temas planı: tanıtımı otomatik aç, hatırlatma balonu göster ya da sus.
  // `shouldHideWidget` bağımlılığı bilinçli: widget login sonrası SPA gezinmesiyle görünür hâle
  // gelir (bkz. CLAUDE.md "Global widgets mounted outside route layouts") — tam sayfa yenilemesi
  // beklenmez. `introPlannedRef` planın yine de yalnız bir kez kurulmasını garanti eder.
  // Tanıtım videosu sürerken plan kurulmaz; `introHeld` bağımlılığı video/uçuş bitince yeniden dener.
  useEffect(() => {
    if (shouldHideWidget || introPlannedRef.current || introHeld) return

    const plan = resolveIntroPlan({
      introSeen: isIntroSeen(),
      nudgeDismissed: isNudgeDismissed(),
      shownThisSession: isShownThisSession(),
      isMobile,
    })
    if (plan === 'none') return

    introPlannedRef.current = true
    markShownThisSession()

    if (plan === 'auto-open') {
      // `introSeen` burada YAZILMAZ — kart gerçekten göründüğünde yazılır (bkz. applyGreeting).
      // `shownThisSession` yukarıda yazıldı; görevi zaten "bu oturumda bir daha tetiklenme".
      introPendingRef.current = true
      setIntroEnter(true)
      introTimerRef.current = window.setTimeout(() => {
        introTimerRef.current = null
        // Bekleme sırasında tanıtım videosu açıldıysa açılış ertelenir (bkz. aşağıdaki serbest bırakma).
        if (introHeldRef.current) {
          deferredAutoOpenRef.current = true
          return
        }
        autoOpenedRef.current = true
        setOpen(true)
      }, INTRO_AUTO_OPEN_DELAY_MS)
      return
    }

    setNudgeVisible(true)
    introTimerRef.current = window.setTimeout(() => setNudgeVisible(false), INTRO_NUDGE_TTL_MS)
  }, [shouldHideWidget, isMobile, introHeld])

  // Video kapanıp logo yerine konunca ertelenen otomatik açılış aynı gecikmeyle yeniden kurulur.
  useEffect(() => {
    if (introHeld || !deferredAutoOpenRef.current) return
    deferredAutoOpenRef.current = false
    if (open) return
    introTimerRef.current = window.setTimeout(() => {
      introTimerRef.current = null
      if (introHeldRef.current) {
        deferredAutoOpenRef.current = true
        return
      }
      autoOpenedRef.current = true
      setOpen(true)
    }, INTRO_AUTO_OPEN_DELAY_MS)
    // `open` bilinçli olarak bağımlılık değil: yalnız bekleme bittiği anda karar verilir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [introHeld])

  // Uçuş yalnız kapalı FAB'e yapılır: panel açıksa ya da widget gizliyse animasyon atlanır.
  useEffect(() => {
    if (introFlight === 'flying' && (open || shouldHideWidget)) setIntroFlight('idle')
  }, [introFlight, open, shouldHideWidget, setIntroFlight])

  // Varış "pop"u tek seferliktir (hareket azaltmada animationend gelmediği için zamanlayıcıyla kalkar).
  useEffect(() => {
    if (!fabArrive) return
    const timer = window.setTimeout(() => setFabArrive(false), FAB_ARRIVE_MS)
    return () => window.clearTimeout(timer)
  }, [fabArrive])

  // Zamanlayıcı YALNIZ unmount'ta temizlenir. Temizlik plan efektinin içine konursa, bağımlılık
  // değişimi (ör. pencere yeniden boyutlanması `isMobile`'ı değiştirir) bekleyen otomatik açılmayı
  // iptal eder; efekt `introPlannedRef` yüzünden erken döndüğü için de bir daha kurulmaz → panel
  // hiç açılmaz. Bu yüzden iki efekt ayrı.
  useEffect(
    () => () => {
      if (introTimerRef.current !== null) window.clearTimeout(introTimerRef.current)
    },
    []
  )

  const isHomepage = location.pathname === '/homepage'

  // Anket durumu panel HER açıldığında ve ana sayfaya her gelişte tazelenir: bayi anketi
  // doldurup döndüğünde ne baloncuk ne "Yeni Sohbet" daveti tekrar çıksın. Hata → davet yok.
  useEffect(() => {
    if (shouldHideWidget || !(open || isHomepage)) return
    // Davet süresi dolduysa servis hiç çağrılmaz.
    if (!isSurveyInviteActive()) {
      setSurveyPending(false)
      return
    }
    let active = true
    new ChatPendingSurveyApi()
      .list()
      .then(data => active && setSurveyPending(hasPendingSurvey(data)))
      .catch(() => active && setSurveyPending(false))
    return () => {
      active = false
    }
  }, [open, isHomepage, shouldHideWidget])

  // Karşılama hangi yoldan gelirse gelsin (taban, ilk temas tanıtımı, "Yeni Sohbet") ve anket
  // yanıtı karşılamadan önce ya da sonra dönsün, davet tek kuralla videonun altına oturur.
  useEffect(() => {
    if (!surveyPending) return
    setMessages(prev => withSurveyInvite(prev, () => idRef.current++))
  }, [surveyPending, messages])

  // panel ilk açıldığında: buton kataloğu + sunucudan sohbet geçmişi.
  // Taze sohbet varsa kaldığı yerden açılır; yoksa karşılama + ActionCards gösterilir (ADR-014).
  useEffect(() => {
    if (!open || loaded) return
    setLoaded(true)

    new ChatButtonsApi()
      .list()
      .then(data => setButtons(normalizeButtons(data)))
      .catch(() => {
        /* buton kataloğu alınamazsa sessiz geç; serbest metin yine çalışır */
      })

    // TABAN DURUMU GARANTİSİ: panel hiçbir zaman balonsuz kalmaz. `applyGreeting` yalnızca mesaj
    // listesi HÂLÂ boşsa karşılama ekler; kullanıcı bu sırada "Yeni Sohbet"e bastıysa (kendi
    // karşılamasını zaten aldıysa) burada ikinci bir karşılama eklenmez — bkz. resumeOnOpen'ın
    // üretici geçersiz kılma / silme yarış durumu düzeltmesi (ChatWidget round-2 fix).
    resumeOnOpen(
      conversationGenerationRef,
      {
        fetchHistoryList: () => new ChatHistoryApi().list(),
        fetchMessages: conversationId => new ChatHistoryMessagesApi(conversationId).list(),
      },
      {
        setHistory,
        applyBubbles: (bubbles, conversationId) => {
          setMessages(bubbles)
          conversationIdRef.current = conversationId
          setActiveConversationId(conversationId)
        },
        applyGreeting: () => {
          // Tanıtım yalnız bu dalda görünür: devam edilecek taze sohbet varsa `applyBubbles`
          // çalışır ve tanıtım atlanır — iki dal birbirinin üstüne yazmaz (ADR-014).
          if (!introPendingRef.current) {
            setMessages(prev => (prev.length > 0 ? prev : [greetingBubble(idRef.current++)]))
            return
          }

          // Bot önce "yazıyor" görünür, sonra karşılama + kart yerine oturur: panel açılır açılmaz
          // hazır bir duvar yerine gelen biri hissi.
          setIntroTyping(true)
          introTimerRef.current = window.setTimeout(() => {
            setIntroTyping(false)
            // Kullanıcı bu sırada "Yeni Sohbet"e bastıysa ya da geçmişten kayıt seçtiyse tanıtım
            // iptal edilmiştir; gecikmiş geri çağrı onun üzerine yazmaz.
            if (!introPendingRef.current) return
            setMessages(prev => (prev.length > 0 ? prev : [introGreetingBubble(idRef.current++)]))
            setShowIntro(true)
            // `introSeen` KART GÖRÜNDÜĞÜNDE yazılır, karar anında değil: aksi hâlde kullanıcı
            // açılışı bekleyen ~3 saniye içinde sayfayı yenilerse tanıtımı hiç görmeden kalıcı
            // olarak kaybeder (geliştirmede HMR de aynı şekilde bayrağı yakıyordu).
            markIntroSeen()
          }, INTRO_TYPING_MS)
        },
      },
      () => idRef.current++
    )
  }, [open, loaded])

  // Faz-44 (spec §7): canlı yanıt gelince bayinin son mesajı panelin üstüne hizalanır; kullanıcı
  // mesajı / "yazıyor" / panel açılışı / geçmiş yüklemesi → bugünkü gibi en alta kaydır.
  // Bağımlılıklar bilinçli olarak son balonun kimliğidir, `messages` değil: yanıta oy vermek balonu
  // yerinde günceller ve bayinin kaydırdığı konumu oynatmamalıdır.
  const lastMessageId = messages.length > 0 ? messages[messages.length - 1].id : null
  const replyAnchorId = replyScrollAnchorId(messages, sending, liveReplyUserId)
  useEffect(() => {
    const panelJustOpened = open && !scrollPanelOpenRef.current
    scrollPanelOpenRef.current = open

    const body = bodyRef.current
    if (!body) return

    const anchor =
      !panelJustOpened && replyAnchorId !== null
        ? body.querySelector<HTMLElement>(`[data-bubble-id="${replyAnchorId}"]`)
        : null
    body.scrollTop = anchor ? anchorScrollTop(body, anchor) : body.scrollHeight
  }, [lastMessageId, messages.length, replyAnchorId, sending, open])

  // panel açılınca girişe odaklan — davetsiz açılan panel hariç: kullanıcı o an başka bir
  // alana yazıyor olabilir, odağı çalmayız.
  useEffect(() => {
    if (!open) return
    if (autoOpenedRef.current) {
      autoOpenedRef.current = false
      return
    }
    requestAnimationFrame(() => inputRef.current?.focus())
  }, [open])

  // tam boyut / mobil modda arka plan kaydırmasını kilitle
  useEffect(() => {
    if (!(open && (expanded || isMobile))) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open, expanded, isMobile])

  // Akış-içi (tam boyut) moddan overlay moduna geçilince açık sidebar'ları kapat:
  // tam boyuttan küçülme veya pencere daralması sonrası sidebar'lar sohbeti örtmesin.
  useEffect(() => {
    if (overlaySidebars && !prevOverlayRef.current) {
      setLeftOpen(false)
      setRightOpen(false)
    }
    prevOverlayRef.current = overlaySidebars
  }, [overlaySidebars])

  // Escape zinciri: overlay sidebar → tam boyuttan çık → paneli kapat
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (activeModal) {
        setActiveModal(null)
      } else if ((leftOpen || rightOpen) && overlaySidebars) {
        setLeftOpen(false)
        setRightOpen(false)
      } else if (expanded && !isMobile) {
        setExpanded(false)
      } else {
        setOpen(false)
        requestAnimationFrame(() => fabRef.current?.focus())
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, activeModal, leftOpen, rightOpen, expanded, isMobile, overlaySidebars])

  const resetInputHeight = () => {
    if (inputRef.current) inputRef.current.style.height = 'auto'
  }

  const sendChatRequest = useCallback(async (request: ChatMessageRequest): Promise<ChatMessageResponse> => {
    // Üretici burada da ilerletilir ve yakalanır (handleSelectHistory ile aynı desen): panel
    // açılışındaki geçmişe otomatik devam fetch'i hâlâ sürerken kullanıcı yeni bir mesaj
    // gönderirse, gecikmiş `applyBubbles` bu isteğin eklediği balonun üzerine yazmamalıdır
    // (bkz. final whole-branch review, Bulgu 2). `isBusy` eşzamanlı gönderimi zaten serileştirir,
    // bu yüzden bir kardeş `sendChatRequest` çağrısının kendi üretici artışını geçersiz kılma
    // riski yoktur.
    const generation = (conversationGenerationRef.current += 1)
    const rawResponse = await new ChatMessageApi().send({
      ...request,
      conversationId: conversationIdRef.current,
    })
    const response = normalizeChatMessageResponse(rawResponse)

    if (generation === conversationGenerationRef.current && response.conversationId > 0) {
      conversationIdRef.current = response.conversationId
      setActiveConversationId(response.conversationId)
    }

    return response
  }, [])

  const dispatchFeatureRequest = useCallback(
    async (request: ChatMessageRequest): Promise<ChatMessageResponse> => {
      setFeatureSending(true)
      try {
        return await sendChatRequest(request)
      } finally {
        setFeatureSending(false)
      }
    },
    [sendChatRequest]
  )

  const dispatchToBackend = async (request: ChatMessageRequest, userEcho?: string, commissionMode?: CommissionMode) => {
    const userBubbleId = userEcho ? addBubble('user', userEcho) : null
    setSending(true)
    try {
      const response = await sendChatRequest(request)
      // Payload'ları balona taşır; payload yoksa bilinen düz-metin kalıplarını karta çevirir
      const { text, extras } = structureChatMessage(response)
      // Kullanıcı görünümü modalda seçti; kartın açılışı backend'in `Mode` echo'suna bırakılmaz.
      // Echo eksik/yanlış gelirse kullanıcı "Tüm oranlar" seçip "En uygun" görünümü almamalı.
      const bubbleExtras =
        commissionMode && extras.commissionRates
          ? { ...extras, commissionRates: { ...extras.commissionRates, mode: commissionMode } }
          : extras
      addBubble('bot', text, { ...bubbleExtras, messageId: response.messageId })
      // Faz-30: sohbetten sepet değiştiyse rozet ve açık sepet sayfası eşitlenir. Geçmiş replay'i bu
      // yoldan geçmez (basketChanged balona taşınmaz) → eski bir mesaj sepeti yeniden çekmez.
      if (response.basketChanged) {
        void refreshBasketAfterChat(
          getBasketApi,
          basket => dispatch(syncActiveBasketRedux(basket)),
          String(ClientStorage.getItem('IsApple')) === '1'
        )
      }
    } catch (error: unknown) {
      addBubble('bot', getChatErrorMessage(error, 'Şu an yanıt veremiyorum, lütfen biraz sonra tekrar deneyin.'))
    } finally {
      // Yanıt (ya da hata cümlesi) geldi: bayinin mesajı panelin üstüne hizalanır (Faz-44).
      setLiveReplyUserId(userBubbleId)
      setSending(false)
    }
  }

  /**
   * Yanıt geri bildirimi (ADR-015). Bayi/kullanıcı kimliği gönderilmez; kapsam oturumdan gelir.
   * Hata BİLEREK yukarı yayılır: `FeedbackBar` iyimser oyu geri alır ya da yorum panelini açık
   * tutar. `ClientProxy` `Success=false` gövdesinde de reddettiği için ek kontrol gerekmez.
   * Yorum yanıtta geri dönmez (`ChatFeedbackState` değişmez).
   */
  const handleFeedback = useCallback(
    async (bubble: ChatBubble, vote: FeedbackVote, reasonCode?: string, comment?: string) => {
      if (!bubble.messageId) return

      const raw = await new ChatFeedbackApi(bubble.messageId).submit(vote, reasonCode, comment)
      const state = normalizeFeedbackState(raw)
      setMessages(prev => prev.map(m => (m.id === bubble.id ? { ...m, feedback: state } : m)))
    },
    []
  )

  const handleSend = () => {
    const text = input.trim()
    if (!text || isBusy) return
    setInput('')
    resetInputHeight()

    if (pending) {
      const p = pending
      const params = { ...(p.carried ?? {}), [p.paramKey]: text }

      // Çok adımlı soru: "En Çok Satın Aldıklarım" B2B seçilirse ardından kapsam sorulur.
      // (SAP'ta kapsam daima bayi olduğu için sorulmaz.) Nihai karar backend'dedir; burada
      // yalnız buton akışını deterministik tutmak için bir sonraki adım açılır.
      const nextStep = NEXT_STEP[p.intent]?.(params)
      if (nextStep) {
        setPending({ intent: p.intent, label: p.label, paramKey: nextStep.paramKey, carried: params })
        const userBubbleId = addBubble('user', text)
        addBubble('bot', nextStep.prompt)
        setLiveReplyUserId(userBubbleId)
        inputRef.current?.focus()
        return
      }

      setPending(null)
      dispatchToBackend({ buttonIntent: p.intent, buttonParameters: params }, text)
      return
    }
    dispatchToBackend({ userText: text }, text)
  }

  const handleButton = (b: ButtonDefinition) => {
    if (isBusy) return
    const featureModal = FEATURE_MODAL_BY_INTENT[b.Intent]
    if (featureModal) {
      setPending(null)
      setLeftOpen(false)
      setRightOpen(false)
      setActiveModal(featureModal)
      return
    }

    if (b.NeedsInput) {
      const paramKey = PARAM_KEY[b.Intent]
      if (paramKey) {
        setPending({ intent: b.Intent, label: b.Label, paramKey })
        addBubble('bot', PENDING_PROMPT[b.Intent] ?? `${b.Label} için bilgiyi yazar mısınız?`)
        inputRef.current?.focus()
        return
      }
      // çoklu parametreli (örn. taksit): doğrudan gönder, tool nazikçe sorar
    }
    setPending(null)
    dispatchToBackend({ buttonIntent: b.Intent }, b.Label)
  }

  const handleFormSelect = (form: ChatForm, opt: FormOption) => {
    if (isBusy) return
    // Faz-48: kendi intent'i olan seçenek o intent'in butonu gibi davranır — modalı olan (Komisyon
    // Oranları) modalı açar, olmayan doğrudan gönderilir; seçimin değeri parametre olarak taşınmaz.
    if (opt.intent) {
      const featureModal = FEATURE_MODAL_BY_INTENT[opt.intent]
      if (featureModal) {
        setPending(null)
        setActiveModal(featureModal)
        return
      }
      dispatchToBackend(
        form.params ? { buttonIntent: opt.intent, buttonParameters: form.params } : { buttonIntent: opt.intent },
        opt.label
      )
      return
    }
    // Bağlam parametreleri (ör. iptal onayında işlem no) seçimle birlikte geri gönderilir;
    // böylece onay adımı LLM'in hatırlamasına bağlı kalmaz.
    const params = {
      ...(form.params ?? {}),
      ...(form.paramKey ? { [form.paramKey]: opt.value } : { value: opt.value }),
    }
    dispatchToBackend({ buttonIntent: form.intent, buttonParameters: params }, opt.label)
  }

  /** Modal yalnız görünümü seçer; sonucu sohbete devreder (kart bot balonunun altında doğar). */
  const handleCommissionSelect = (mode: CommissionMode, label: string) => {
    setActiveModal(null)
    dispatchToBackend({ buttonIntent: COMMISSION_INTENT, buttonParameters: { mode } }, label, mode)
  }

  const handleNewChat = () => {
    if (isBusy) return
    conversationGenerationRef.current += 1
    conversationIdRef.current = null
    setActiveConversationId(null)
    setPending(null)
    setActiveModal(null)
    setInput('')
    // "Yeni Sohbet" tanıtımı geri getirmez.
    introPendingRef.current = false
    setShowIntro(false)
    setIntroTyping(false)
    resetInputHeight()
    // Karşılama yine index 0'daki bot mesajı olur → ActionCards yeniden görünür
    setMessages([greetingBubble(idRef.current++)])
    if (overlaySidebars) setLeftOpen(false)
    inputRef.current?.focus()
  }

  const handleSelectHistory = async (conversationId: number) => {
    if (isBusy) return

    // Üretici burada ilerletilir ve yakalanır: kullanıcı bekleme sırasında başka bir kayıt
    // seçerse (veya "Yeni Sohbet"e basarsa) bu isteğin sonucu asla ekrana yansımaz — en son
    // seçim her zaman kazanır (bkz. applyIfCurrentGeneration / ADR-014 yarış durumu düzeltmesi).
    const generation = (conversationGenerationRef.current += 1)
    setPending(null)
    introPendingRef.current = false
    setShowIntro(false)
    setIntroTyping(false)
    setActiveModal(null)
    if (overlaySidebars) setLeftOpen(false)

    try {
      await applyIfCurrentGeneration(
        generation,
        conversationGenerationRef,
        () => new ChatHistoryMessagesApi(conversationId).list(),
        raw => {
          const bubbles = historyMessagesToBubbles(raw, () => idRef.current++)
          if (bubbles.length === 0) {
            addBubble('bot', 'Bu sohbetin içeriği artık görüntülenemiyor.')
            return
          }
          setMessages(bubbles)
          conversationIdRef.current = conversationId
          setActiveConversationId(conversationId)
        }
      )
    } catch (error: unknown) {
      // Geçersiz kılınmış (bayat) bir isteğin hatası da ekrana yansıtılmaz.
      if (isGenerationCurrent(generation, conversationGenerationRef.current)) {
        addBubble('bot', getChatErrorMessage(error, 'Sohbet geçmişi şu anda yüklenemiyor.'))
      }
    }
  }

  const handleDeleteHistory = async (conversationId: number) => {
    if (isBusy) return
    // Üretici burada da ilerletilir: silinen kayıt için hâlâ bekleyen bir handleSelectHistory
    // isteği varsa (kullanıcı önce seçip sonra sildiyse) sonucu geçersiz kılınır — silinmiş bir
    // sohbet, gecikmiş bir fetch sayesinde ekrana geri gelemez.
    conversationGenerationRef.current += 1
    try {
      await new ChatHistoryDeleteApi().remove(conversationId)
      setHistory(prev => prev.filter(h => h.conversationId !== conversationId))
      // Silinen sohbet ekrandaysa temiz bir sohbete dön.
      if (conversationIdRef.current === conversationId) handleNewChat()
    } catch (error: unknown) {
      addBubble('bot', getChatErrorMessage(error, 'Sohbet şu anda silinemiyor.'))
    }
  }

  const toggleLeft = () => {
    setLeftOpen(v => {
      const next = !v
      if (next && overlaySidebars) setRightOpen(false)
      return next
    })
  }

  const toggleRight = () => {
    setRightOpen(v => {
      const next = !v
      if (next && overlaySidebars) setLeftOpen(false)
      return next
    })
  }

  // Karşılama altındaki CTA: sağ "Hızlı Bağlantılar" sekmesini açar (toggle değil).
  const openRight = () => {
    setRightOpen(true)
    if (overlaySidebars) setLeftOpen(false)
  }

  /** Bekleyen otomatik açılma / balon zamanlayıcısını iptal eder. */
  const clearIntroTimer = () => {
    if (introTimerRef.current === null) return
    window.clearTimeout(introTimerRef.current)
    introTimerRef.current = null
  }

  /**
   * FAB: kullanıcı 2 sn dolmadan kendisi bastıysa bekleyen otomatik açılma iptal edilir — yoksa
   * zamanlayıcı sonradan `autoOpenedRef`'i kirletir ve bir sonraki elle açılışta odak atlanır.
   * `introPendingRef` korunduğu için tanıtım kartı yine gösterilir.
   */
  const handleFabClick = () => {
    // Sürüklemenin bırakışından gelen tık paneli açmaz.
    if (fabDrag.consumeDragClick()) return
    clearIntroTimer()
    deferredAutoOpenRef.current = false
    setOpen(true)
  }

  /** Uçuşun hedefi: FAB'in o anki kutusu (gizli ama yerleşimde; sürüklenmiş olabilir). */
  const getFlightTarget = () => fabRef.current?.getBoundingClientRect() ?? null

  /** Logo FAB'e kondu: durum `idle`, FAB "pop" ile görünür. */
  const handleFlightDone = () => {
    setIntroFlight('idle')
    setFabArrive(true)
  }

  /**
   * Tanıtım kartındaki tema tıklaması. `ask` serbest metin sorgusu atar (sahte akış kurulmaz);
   * `prompt` parametre isteyen alanlar için mevcut `pending` akışını açar — buton akışıyla aynı
   * mekanizma, nihai kararı yine backend verir.
   */
  const handleIntroTheme = (action: IntroThemeAction) => {
    if (isBusy) return
    if (action.kind === 'ask') {
      setPending(null)
      dispatchToBackend({ userText: action.question }, action.question)
      return
    }
    setPending({ intent: action.intent, label: action.label, paramKey: action.paramKey })
    addBubble('bot', action.message)
    inputRef.current?.focus()
  }

  /** Hatırlatma balonuna dokunuldu: tanıtımı hiç görmemiş hesap (mobil yolu) kartı burada görür. */
  const handleNudgeOpen = () => {
    clearIntroTimer()
    setNudgeVisible(false)
    // `introSeen` burada da yazılmaz; kart göründüğünde `applyGreeting` yazar.
    if (!isIntroSeen()) introPendingRef.current = true
    autoOpenedRef.current = false
    deferredAutoOpenRef.current = false
    setOpen(true)
  }

  const handleNudgeDismiss = () => {
    clearIntroTimer()
    setNudgeVisible(false)
    markNudgeDismissed()
  }

  /** Anket baloncuğu: ankete gidildi ya da X'e basıldı → bu sekmede bir daha çıkmaz. */
  const closeSurveyNudge = () => {
    markSurveyNudgeClosed()
    setSurveyNudgeClosed(true)
  }

  const closeWidget = () => {
    setActiveModal(null)
    setOpen(false)
  }

  const minimizeWidget = () => {
    setOpen(false)
    requestAnimationFrame(() => fabRef.current?.focus())
  }

  if (shouldHideWidget) return null

  const showScrim = open && (leftOpen || rightOpen) && overlaySidebars
  /** Logo ekran ortasından FAB'e uçuyor; FAB yerleşimde kalır ama görünmez. */
  const showFlight = introFlight === 'flying' && !open

  const fabClass = [
    classes.fab,
    classes.tipLeft,
    fabDrag.dragging ? classes.fabDragging : '',
    showFlight ? classes.fabHidden : '',
    fabArrive ? classes.fabArrive : '',
  ]
    .filter(Boolean)
    .join(' ')

  const panelClass = [
    classes.panel,
    expanded ? classes.panelExpanded : classes.panelCompact,
    introEnter ? classes.panelIntroEnter : '',
    leftOpen ? classes.leftOpen : '',
    rightOpen ? classes.rightOpen : '',
  ]
    .filter(Boolean)
    .join(' ')

  // Panel kapalıyken yalnız logo ve baloncuklar görünür: popup/modal/uyarıların ARKASINDA kalır.
  const rootClass = open ? classes.root : `${classes.root} ${classes.rootIdle}`

  return (
    <div
      ref={rootRef}
      className={rootClass}
      style={fabDrag.rootStyle}
      data-side-x={fabDrag.sideX}
      data-side-y={fabDrag.sideY}
    >
      {!open && (
        <>
          {/* Baloncuklar Gini Tanıtım videosu ve logo uçuşu sürerken beklemede kalır. */}
          {nudgeVisible && !introHeld && <IntroNudge onOpen={handleNudgeOpen} onDismiss={handleNudgeDismiss} />}
          {/* Anket baloncuğu yalnız ana sayfada; tanıtım balonu / otomatik açılış sürerken beklemede kalır. */}
          {isHomepage && surveyPending && !surveyNudgeClosed && !nudgeVisible && !introEnter && !introHeld && (
            <SurveyNudge onGo={closeSurveyNudge} onDismiss={closeSurveyNudge} />
          )}
          {showFlight && <GiniIntroFlightOverlay getTarget={getFlightTarget} onDone={handleFlightDone} />}
          <button
            ref={fabRef}
            className={fabClass}
            data-tip='B-Gini'
            onClick={handleFabClick}
            {...fabDrag.fabHandlers}
            aria-label='B-Gini asistanı aç'
            aria-keyshortcuts='Alt+ArrowUp Alt+ArrowDown Alt+ArrowLeft Alt+ArrowRight'
          >
            <FabGlow />
            <span className={classes.fabIcon}>
              <IconBot />
            </span>
          </button>
        </>
      )}

      {open && expanded && !isMobile && (
        <div className={classes.backdrop} onClick={() => setExpanded(false)} aria-hidden='true' />
      )}

      {open && (
        <div
          className={panelClass}
          role='dialog'
          aria-modal={expanded || isMobile ? true : undefined}
          aria-label='B-Gini'
          // Sınıf tek seferlik: panelin içindeki animasyonlu öğelerden kabaran olayları eleyip
          // yalnız panelin kendi animasyonu bittiğinde temizlenir (yoksa yeniden tetiklenir).
          onAnimationEnd={e => {
            if (e.target === e.currentTarget) setIntroEnter(false)
          }}
        >
          <HistorySidebar
            open={leftOpen}
            sessions={history}
            activeConversationId={activeConversationId}
            onNewChat={handleNewChat}
            onSelect={handleSelectHistory}
            onDelete={handleDeleteHistory}
            disabled={isBusy}
          />

          <div className={classes.main}>
            <ChatHeader
              expanded={expanded}
              leftOpen={leftOpen}
              rightOpen={rightOpen}
              onToggleLeft={toggleLeft}
              onToggleRight={toggleRight}
              onToggleExpand={() => setExpanded(v => !v)}
              onMinimize={minimizeWidget}
              onClose={closeWidget}
            />

            <div className={classes.body} ref={bodyRef} aria-live='polite'>
              <div className={classes.messagesInner}>
                {messages.map((m, idx) => (
                  <Fragment key={m.id}>
                    <MessageBubble
                      bubble={m}
                      onFormSelect={handleFormSelect}
                      onNavigate={minimizeWidget}
                      onPlayVideo={video => {
                        setPromoVideo(video)
                        setActiveModal('promoVideo')
                      }}
                      onFeedback={handleFeedback}
                      disabled={isBusy}
                    />

                    {/* İlk temas: karşılamanın (varsa anket davetinin) altında tanıtım kartı (CTA'nın yerine) */}
                    {idx === welcomeAnchorIdx && m.role === 'bot' && showIntro && (
                      <IntroCard onTheme={handleIntroTheme} onShowAll={openRight} disabled={isBusy} />
                    )}

                    {/* Karşılamanın hemen altında: hızlı bağlantılar sekmesini açan tek CTA */}
                    {idx === welcomeAnchorIdx && m.role === 'bot' && !showIntro && buttons.length > 0 && (
                      <button
                        type='button'
                        className={classes.quickLinksCta}
                        style={{ animationDelay: '100ms' }}
                        onClick={openRight}
                      >
                        <span className={classes.quickLinksCtaIcon}>
                          <IconStar />
                        </span>
                        <span className={classes.quickLinksCtaLabel}>Hızlı Bağlantılar</span>
                        <span className={classes.quickLinksCtaChevron}>
                          <IconChevron />
                        </span>
                      </button>
                    )}
                  </Fragment>
                ))}

                {introTyping && <TypingIndicator label='B-Gini bağlanıyor…' />}
                {sending && <TypingIndicator />}
              </div>
            </div>

            <ChatInput
              value={input}
              onChange={setInput}
              onSend={handleSend}
              sending={isBusy}
              placeholder={pending ? `${pending.label}…` : 'Mesajınızı buraya yazın…'}
              inputRef={inputRef}
            />
          </div>

          <QuickLinksSidebar open={rightOpen} buttons={buttons} onButton={handleButton} disabled={isBusy} />

          {showScrim && (
            <div
              className={classes.scrim}
              onClick={() => {
                setLeftOpen(false)
                setRightOpen(false)
              }}
              aria-hidden='true'
            />
          )}

          {activeModal === 'cargo' && (
            <CargoTrackingModal onClose={() => setActiveModal(null)} onRequest={dispatchFeatureRequest} />
          )}
          {activeModal === 'serialNumber' && (
            <SerialNumberCheckModal onClose={() => setActiveModal(null)} onRequest={dispatchFeatureRequest} />
          )}
          {activeModal === 'guarantee' && (
            <GuaranteeInquiryModal onClose={() => setActiveModal(null)} onRequest={dispatchFeatureRequest} />
          )}
          {activeModal === 'commission' && (
            <CommissionRatesModal onClose={() => setActiveModal(null)} onSelect={handleCommissionSelect} />
          )}
          {activeModal === 'promoVideo' && promoVideo && (
            <PromoVideoModal
              video={promoVideo}
              onClose={() => {
                setActiveModal(null)
                setPromoVideo(null)
              }}
            />
          )}
        </div>
      )}
    </div>
  )
}
